/**
 * Firebase Read Monitor
 * Tracks and logs all Firestore operations to identify excessive reads
 */
import { isDevToolsGateOpen } from './devToolsGate';

class FirebaseMonitor {
  constructor() {
    this.reads = [];
    this.writes = [];
    this.listeners = new Map();
    this.startTime = Date.now();

    if (isDevToolsGateOpen()) {
      setInterval(() => this.logSummary(), 30000);
    }
  }

  logRead(collection, operation, documentCount = 1, source = 'unknown') {
    const entry = {
      timestamp: new Date().toISOString(),
      collection,
      operation,
      documentCount,
      source,
      stackTrace: new Error().stack
    };

    this.reads.push(entry);

    if (isDevToolsGateOpen()) {
      console.log(`FIREBASE READ: ${collection} (${operation}) - ${documentCount} docs from ${source}`);
    }

    // Alert if excessive reads
    if (documentCount > 100) {
      console.warn(`⚠️ LARGE READ: ${documentCount} documents from ${collection}`);
      console.trace();
    }
  }

  logWrite(collection, operation, source = 'unknown') {
    const entry = {
      timestamp: new Date().toISOString(),
      collection,
      operation,
      source
    };

    this.writes.push(entry);
    if (isDevToolsGateOpen()) {
      console.log(`FIREBASE WRITE: ${collection} (${operation}) from ${source}`);
    }
  }

  logListener(collection, listenerId, operation = 'subscribe') {
    if (operation === 'subscribe') {
      this.listeners.set(listenerId, {
        collection,
        subscribedAt: new Date().toISOString()
      });
      if (isDevToolsGateOpen()) {
        console.log(`LISTENER SUBSCRIBED: ${collection} (ID: ${listenerId})`);
      }
    } else if (operation === 'unsubscribe') {
      this.listeners.delete(listenerId);
      if (isDevToolsGateOpen()) {
        console.log(`LISTENER UNSUBSCRIBED: ${collection} (ID: ${listenerId})`);
      }
    }
  }

  logSummary() {
    const timeElapsed = Math.round((Date.now() - this.startTime) / 1000);
    const recentReads = this.reads.filter(r =>
      new Date(r.timestamp) > new Date(Date.now() - 30000)
    );

    // Group reads by collection
    const readsByCollection = {};
    let totalReads = 0;

    recentReads.forEach(read => {
      if (!readsByCollection[read.collection]) {
        readsByCollection[read.collection] = {
          count: 0,
          documents: 0,
          operations: []
        };
      }
      readsByCollection[read.collection].count++;
      readsByCollection[read.collection].documents += read.documentCount;
      readsByCollection[read.collection].operations.push(read.operation);
      totalReads += read.documentCount;
    });

    console.log(`\n📊 FIREBASE MONITOR SUMMARY (Last 30s)`);
    console.log(`⏱️ Time elapsed: ${timeElapsed}s`);
    console.log(`📖 Total reads: ${totalReads} documents`);
    console.log(`👂 Active listeners: ${this.listeners.size}`);

    if (Object.keys(readsByCollection).length > 0) {
      console.log(`\n📚 Reads by collection:`);
      Object.entries(readsByCollection).forEach(([collection, data]) => {
        console.log(`  ${collection}: ${data.documents} docs (${data.count} operations)`);
      });
    }

    if (totalReads > 1000) {
      console.error(`\n🚨 WARNING: ${totalReads} reads in last 30 seconds!`);
      console.log(`Top sources:`);

      // Find the most common sources
      const sources = {};
      recentReads.forEach(read => {
        sources[read.source] = (sources[read.source] || 0) + read.documentCount;
      });

      Object.entries(sources)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .forEach(([source, count]) => {
          console.log(`  ${source}: ${count} reads`);
        });
    }

    // Reset for next period
    this.reads = this.reads.filter(r =>
      new Date(r.timestamp) > new Date(Date.now() - 60000) // Keep last minute
    );
  }

  getStats() {
    return {
      totalReads: this.reads.reduce((sum, r) => sum + r.documentCount, 0),
      totalWrites: this.writes.length,
      activeListeners: this.listeners.size,
      readsByCollection: this.getReadsByCollection(),
      uptime: Math.round((Date.now() - this.startTime) / 1000)
    };
  }

  getReadsByCollection() {
    const result = {};
    this.reads.forEach(read => {
      if (!result[read.collection]) {
        result[read.collection] = 0;
      }
      result[read.collection] += read.documentCount;
    });
    return result;
  }
}

// Create singleton instance
export const firebaseMonitor = new FirebaseMonitor();

// Make it globally available for debugging
if (isDevToolsGateOpen()) {
  window.firebaseMonitor = firebaseMonitor;
  console.log('Firebase Monitor initialized. Access via window.firebaseMonitor.getStats()');
}
